"use client";

import * as React from "react";
import { Controller, useForm, useFormState } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button, FieldError, Input, Label, Modal, Spinner, TextField, toast } from "@heroui/react";
import { TreeSelect, type TreeSelectNode } from "@/components/custom";
import { trpc } from "@/lib/trpc";
import { buildTree, type TreeNode } from "@hs/shared";
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const peopleSchema = z.object({
  primaryOrgId: z.string().trim().min(1, "请选择所在主组织"),
  fullName: z.string().trim().min(1, "请填写姓名").max(100, "姓名不能超过100个字符"),
  jobNumber: z.string().trim().min(1, "请填写工号").max(32, "工号不能超过32个字符"),
  mobile: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" || val == null ? null : val))
    .refine((val) => !val || /^1[3-9]\d{9}$/.test(val), "请输入正确的手机号（11位数字，以1开头）"),
  email: z
    .union([z.string(), z.null()])
    .transform((val) => (val === "" || val == null ? null : val))
    .refine((val) => !val || z.string().email().safeParse(val).success, "请输入正确的邮箱地址"),
});

type PeopleFormValues = z.infer<typeof peopleSchema>;

export type PeopleEditorMode = "create" | "edit";

export interface PeopleEditorDialogProps {
  open: boolean;
  mode?: PeopleEditorMode;
  peopleData?: Record<string, unknown>;
  defaultOrgIds?: string[];
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function orgRecordsToTreeSelectNodes(orgs: Record<string, unknown>[]): TreeSelectNode[] {
  return orgs.map((org) => ({
    id: String(org.id),
    label: String(org.shortName || org.fullName || ""),
    children: org.children
      ? orgRecordsToTreeSelectNodes(org.children as Record<string, unknown>[])
      : undefined,
  }));
}

export function PeopleEditorDialog({
  open,
  mode = "create",
  peopleData,
  defaultOrgIds,
  onOpenChange,
  onSuccess,
}: PeopleEditorDialogProps) {
  const utils = trpc.useUtils();
  const isEdit = mode === "edit";

  const { data: orgTreeData, isLoading: isLoadingOrgTree } = trpc.contacts.findAllSpaceTrees.useQuery(
    undefined,
    { enabled: open, staleTime: 0 }
  );

  const fullPersonQuery = trpc.people.get.useQuery(
    { id: (peopleData?.id as string) ?? "" },
    { enabled: open && isEdit && !!peopleData?.id }
  );
  const editSource = (fullPersonQuery.data ?? peopleData) as Record<string, unknown> | undefined;

  const orgOptions: TreeSelectNode[] = React.useMemo(() => {
    if (!orgTreeData?.length) return [];
    const valid = orgTreeData.filter((o) => o.id != null);
    const flatForTree: TreeNode[] = valid.map((org) => ({
      id: String(org.id),
      parentId: org.parentId ?? null,
      shortName: org.shortName,
      fullName: org.fullName,
      type: org.type,
      order: org.order ?? 0,
    }));
    const tree = buildTree(flatForTree, { parentField: "parentId", sortBy: "order", sortOrder: "asc" });
    return orgRecordsToTreeSelectNodes(tree as Record<string, unknown>[]);
  }, [orgTreeData]);

  const form = useForm<PeopleFormValues>({
    resolver: zodResolver(peopleSchema),
    /** 不在首个错误字段上抢焦点，便于同时看到所有 invalid + FieldError */
    shouldFocusError: false,
    defaultValues: {
      primaryOrgId: defaultOrgIds?.[0] ?? "",
      fullName: "",
      jobNumber: "",
      mobile: null,
      email: null,
    },
  });

  const { errors } = useFormState({ control: form.control });

  const createPeopleMutation = trpc.contacts.createPerson.useMutation({
    onSuccess: () => {
      toast.success("人员创建成功");
      form.reset();
      onOpenChange(false);
      onSuccess();
      void utils.contacts.getPeopleByOrgId.invalidate();
    },
  });

  const updatePeopleMutation = trpc.contacts.updatePerson.useMutation({
    onSuccess: () => {
      toast.success("人员更新成功");
      form.reset();
      onOpenChange(false);
      onSuccess();
      void utils.contacts.getPeopleByOrgId.invalidate();
    },
  });

  React.useEffect(() => {
    if (!open) {
      form.reset();
      return;
    }
    if (isEdit && editSource) {
      const orgs = editSource.orgs as { orgId: string; isPrimary?: boolean }[] | undefined;
      const primaryFromOrgs = orgs?.find((o) => o.isPrimary)?.orgId;
      const primaryOrgId =
        (editSource.primaryOrgId as string | null | undefined) ??
        primaryFromOrgs ??
        defaultOrgIds?.[0] ??
        "";
      form.reset({
        primaryOrgId,
        fullName: (editSource.fullName as string) || "",
        jobNumber: (editSource.jobNumber as string | null) ?? "",
        mobile: (editSource.mobile as string | null) ?? null,
        email: (editSource.email as string | null) ?? null,
      });
    } else {
      form.reset({
        primaryOrgId: defaultOrgIds?.[0] ?? "",
        fullName: "",
        jobNumber: "",
        mobile: null,
        email: null,
      });
    }
  }, [open, mode, peopleData, editSource, defaultOrgIds, form, isEdit]);

  const onSubmit = async (values: PeopleFormValues) => {
    const pid = values.primaryOrgId;
    const submitData = {
      primaryOrgId: pid,
      orgIds: pid ? [pid] : undefined,
      fullName: values.fullName,
      jobNumber: values.jobNumber,
      mobile: values.mobile?.trim() || null,
      email: values.email?.trim() || null,
    };
    if (isEdit && (peopleData ?? editSource)) {
      const editId = (peopleData?.id ?? editSource?.id) as string | undefined;
      if (!editId) return;
      await updatePeopleMutation.mutateAsync({ id: editId, data: submitData });
    } else {
      await createPeopleMutation.mutateAsync(submitData);
    }
  };

  const dialogTitle = isEdit ? "编辑人员" : "新建人员";
  const submitButtonText = isEdit ? "保存" : "确定";
  const isSubmitting = createPeopleMutation.isPending || updatePeopleMutation.isPending;

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) form.reset();
      onOpenChange(next);
    },
    [form, onOpenChange]
  );

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="md">
          <Modal.Dialog className="max-w-md">
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">{dialogTitle}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="gap-4 p-1">
              <form id="people-editor-form" className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
                <TextField
                  className="w-full"
                  isDisabled={isSubmitting}
                  isInvalid={!!errors.primaryOrgId}
                  isRequired
                  name="primaryOrgId"
                  validationBehavior="aria"
                >
                  <Label className="text-sm font-medium">所在主组织</Label>
                  <Controller
                    control={form.control}
                    name="primaryOrgId"
                    render={({ field }) => (
                      <TreeSelect
                        ref={field.ref}
                        aria-label="选择所在主组织"
                        disabled={isSubmitting}
                        emptyMessage="暂无组织数据"
                        isInvalid={!!errors.primaryOrgId}
                        isLoading={isLoadingOrgTree}
                        isRequired
                        items={orgOptions}
                        placeholder="选择所在主组织"
                        selectionMode="single"
                        value={field.value || null}
                        onBlur={field.onBlur}
                        onChange={(v) => field.onChange(typeof v === "string" ? v : "")}
                      />
                    )}
                  />
                  {errors.primaryOrgId ? <FieldError>{errors.primaryOrgId.message}</FieldError> : null}
                </TextField>

                <TextField
                  className="w-full"
                  isDisabled={isSubmitting}
                  isInvalid={!!errors.fullName}
                  isRequired
                  name="fullName"
                  validationBehavior="aria"
                >
                  <Label className="text-sm font-medium">姓名</Label>
                  <Controller
                    control={form.control}
                    name="fullName"
                    render={({ field }) => (
                      <Input
                        placeholder="请输入姓名"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  />
                  {errors.fullName ? <FieldError>{errors.fullName.message}</FieldError> : null}
                </TextField>

                <TextField
                  className="w-full"
                  isDisabled={isSubmitting}
                  isInvalid={!!errors.jobNumber}
                  isRequired
                  name="jobNumber"
                  validationBehavior="aria"
                >
                  <Label className="text-sm font-medium">工号</Label>
                  <Controller
                    control={form.control}
                    name="jobNumber"
                    render={({ field }) => (
                      <Input
                        placeholder="请输入工号"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  />
                  {errors.jobNumber ? <FieldError>{errors.jobNumber.message}</FieldError> : null}
                </TextField>

                <TextField
                  className="w-full"
                  isDisabled={isSubmitting}
                  isInvalid={!!errors.mobile}
                  name="mobile"
                  validationBehavior="aria"
                >
                  <Label className="text-sm font-medium">手机号</Label>
                  <Controller
                    control={form.control}
                    name="mobile"
                    render={({ field }) => (
                      <Input
                        placeholder="请输入手机号"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  />
                  {errors.mobile ? <FieldError>{errors.mobile.message}</FieldError> : null}
                </TextField>

                <TextField
                  className="w-full"
                  isDisabled={isSubmitting}
                  isInvalid={!!errors.email}
                  name="email"
                  validationBehavior="aria"
                >
                  <Label className="text-sm font-medium">邮箱</Label>
                  <Controller
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <Input
                        placeholder="请输入邮箱"
                        type="email"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  />
                  {errors.email ? <FieldError>{errors.email.message}</FieldError> : null}
                </TextField>
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="ghost" onPress={() => handleModalOpenChange(false)} isDisabled={isSubmitting}>
                取消
              </Button>
              <Button
                form="people-editor-form"
                isDisabled={isSubmitting}
                type="submit"
                variant="primary"
              >
                {isSubmitting ? <Spinner className="mr-2 size-4" /> : null}
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
